import { questionnaireManager } from "@/services/questionnaire/questionnaire-manager";
import { defineStore } from "pinia";
import { useTriadStore } from "@/store/triad/triad";
import { useMainStore } from "@/store/main/main";
import { userManager } from "@/services/security/user-manager";

export const useQuestionnaireStore = defineStore({
  id: "Questionnaire",
  state: () => {
    return {
      questionnaires: [],
      questionnaire: {},
      model: {},
      toSign: [],
      toComplete: [],
      questionnaireLoading: false,
      userProgression: 0,
      triadProgression: 0,
      total: 0,
      totalTriad: 0,
    };
  },

  actions: {
    updateQuestionnaireStatus(status) {
      if (this.questionnaire?.userReply) {
        this.questionnaire.userReply["status"] = status;
      }
    },

    fetchQuestionnaires(id) {
      return questionnaireManager.loadQuestionnaires(id).then((questionnaires) => {
        this.questionnaires = questionnaires;
        this.setQuestionnaireToDoAndProgress(useTriadStore().triad);
      });
    },

    fetchQuestionnaire(payload) {
      if (!payload.refresh) {
        this.setQuestionnaireRequest();
      }
      if (useMainStore().isInternet) {
        this.model = [];
        return questionnaireManager
          .loadQuestionnaire(payload.questionnaire, payload.triad)
          .then((questionnaire) => {
            if (questionnaire) {
              this.initQuestionnaireDatas(questionnaire);
            }
          })
          .finally(() => {
            this.setQuestionnaireSuccess();
          });
      } else {
        return Promise.resolve();
      }
    },

    initQuestionnaireDatas(questionnaire) {
      this.questionnaire = questionnaire;
      this.model = questionnaireManager.buildModel(this.questionnaire);
      this.setQuestionnaireSuccess();
    },

    setQuestionnaireToDoAndProgress(triad) {
      this.questionnaires = triad.questionnaires;
      this.toComplete = [];
      this.toSign = [];
      this.totalTriad = 0;
      this.total = 0;
      this.userProgression = 0;
      this.triadProgression = 0;
      const profile = triad.userRoles ? userManager.getProfileKey(triad.userRoles[0]) : null;
      const minContractStart = triad.contracts
        ? (Object.values(triad.contracts)
            .map((c) => c.startDate)
            .filter(Boolean)
            .sort()[0] ?? null)
        : null;
      if (triad.questionnaires && Object.keys(triad.questionnaires).length) {
        Object.values(triad.questionnaires).forEach((year) => {
          // Le cutoff (contrat démarrant le 1er juillet inclus => nouvelle année scolaire) n'exclut
          // l'année QUE des compteurs du taux de remplissage, pas des listes toComplete/toSign
          // (elles restent pilotées par les flags questionnaire, gérés par ailleurs).
          const yearCountsForStats = !(minContractStart && `${year.year + 1}-07-01` <= minContractStart);
          Object.values(year.categories).forEach((category) => {
            Object.values(category.originalQuestionnaires).forEach((questionnaire) => {
              if (
                !questionnaire.isCopy &&
                (!questionnaire.canBeCopiedBy || (questionnaire.canBeCopiedBy && !questionnaire.canBeCopiedBy.length))
              ) {
                if (questionnaire.userHasToComplete) {
                  this.toComplete.push({
                    title: questionnaire.title,
                    date: questionnaire.completenessDate
                      ? questionnaire.completenessDate
                      : questionnaire.profileStatus?.[profile]?.endDate,
                    id: questionnaire.id,
                    isUsingSkill: questionnaire.isUsingSkill,
                  });
                } else if (questionnaire.userHasToSign) {
                  this.toSign.push({
                    title: questionnaire.title,
                    date: questionnaire.completenessDate
                      ? questionnaire.completenessDate
                      : questionnaire.profileStatus?.[profile]?.endDate,
                    id: questionnaire.id,
                  });
                }

                if (!yearCountsForStats) {
                  return;
                }

                if (questionnaire.userIsSignatory) {
                  this.total++;
                }

                if (questionnaire.userHasSigned) {
                  this.userProgression++;
                }

                this.totalTriad += questionnaire.signedBy.length;

                Object.values(questionnaire.lastReply).forEach((reply) => {
                  if (reply && reply.signedAt) {
                    this.triadProgression++;
                  }
                });
              }
            });
          });
        });
      }
    },

    setAnswer(payload) {
      if (this.model[payload.id]) {
        if (payload.value) {
          this.model[payload.id].vars.value = payload.value;
        }
        if (payload.comments) {
          this.model[payload.id].vars.comments = payload.comments;
        }
      }
    },

    // Met à jour le contractMission d'une évolution de mission (itemModel.missionEvolutions),
    // sans que le composant appelant n'ait à muter directement cette prop dérivée du store.
    updateMissionContractMission({ itemModel, missionId, patch }) {
      const evolution = (itemModel?.missionEvolutions || []).find((item) => item.contractMission?.id === missionId);
      if (evolution?.contractMission) {
        Object.assign(evolution.contractMission, patch);
      }
    },

    updateActivityContractActivity({ itemModel, activityId, patch }) {
      const evolution = (itemModel?.activityEvolutions || []).find((item) => item.contractActivity?.id === activityId);
      if (evolution?.contractActivity) {
        Object.assign(evolution.contractActivity, patch);
      }
    },

    init() {
      this.questionnaires = [];
      this.questionnaire = {};
      this.model = {};
      this.toSign = [];
      this.toComplete = [];
      this.questionnaireLoading = false;
      this.userProgression = 0;
      this.triadProgression = 0;
      this.total = 0;
      this.totalTriad = 0;
    },

    setQuestionnaireRequest() {
      this.questionnaireLoading = true;
    },

    setQuestionnaireSuccess() {
      this.questionnaireLoading = false;
    },

    setToSign(data) {
      this.toSign = data;
    },

    setToComplete(data) {
      this.toComplete = data;
    },

    setQuestionnaireMarks(questionnaire) {
      if (questionnaire?.questions && Object.keys(questionnaire.questions).length) {
        questionnaire.questions.forEach((question) => {
          let questionnaireQuestion;
          if (this.questionnaire?.questions && Object.keys(this.questionnaire.questions).length) {
            questionnaireQuestion = Object.values(this.questionnaire.questions).find(
              (questionSearch) => questionSearch.id === question.id,
            );
          }
          if (!question.mark) {
            questionnaireQuestion.mark = null;
          }
          if (questionnaireQuestion && Object.keys(questionnaireQuestion).length && Array.isArray(question?.mark)) {
            questionnaireQuestion.mark = [
              ...new Map(question.mark.map((item) => [item.profile + "-" + item.mark, item])).values(),
            ];
          }
        });

        this.questionnaire.mark = questionnaire.mark;
      }
    },
  },
});
